import type { CoinType } from '../../types'

type Props = {
  type: CoinType | string
  size?: number
  className?: string
}

const PALETTE = {
  gold: {
    rim: '#E8A812',
    rimLight: '#FFD84A',
    face: '#FFC933',
    faceDark: '#D4920E',
    mark: '#FFF1A8',
    markDark: '#B8740A',
    spark: '#FFFFFF',
  },
  silver: {
    rim: '#8E9BB0',
    rimLight: '#D7DEEA',
    face: '#C5CEDC',
    faceDark: '#7E8BA0',
    mark: '#F4F7FC',
    markDark: '#5E6B80',
    spark: '#FFFFFF',
  },
  bronze: {
    rim: '#A85A22',
    rimLight: '#E09048',
    face: '#D07830',
    faceDark: '#8A4518',
    mark: '#F0B070',
    markDark: '#6E3010',
    spark: '#FFE8C8',
  },
} as const

function CoinMetal({
  size,
  colors,
  className,
}: {
  size: number
  colors: (typeof PALETTE)[keyof typeof PALETTE]
  className?: string
}) {
  const id = `coin-${colors.rim.replace('#', '')}`
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      aria-hidden="true"
      style={{ display: 'block', background: 'transparent' }}
    >
      <defs>
        <radialGradient id={`${id}-face`} cx="35%" cy="30%" r="70%">
          <stop offset="0%" stopColor={colors.rimLight} />
          <stop offset="55%" stopColor={colors.face} />
          <stop offset="100%" stopColor={colors.faceDark} />
        </radialGradient>
        <linearGradient id={`${id}-rim`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={colors.rimLight} />
          <stop offset="100%" stopColor={colors.rim} />
        </linearGradient>
      </defs>
      {/* coin body — fills almost whole viewBox, no outer square frame */}
      <circle cx="32" cy="32" r="30" fill={`url(#${id}-rim)`} />
      <circle cx="32" cy="32" r="24" fill={`url(#${id}-face)`} />
      <circle cx="32" cy="32" r="22" fill="none" stroke={colors.markDark} strokeOpacity="0.25" strokeWidth="1.5" />
      {/* crown mark */}
      <path
        d="M18 40 V28 L24 34 L32 20 L40 34 L46 28 V40 H40 V36 H24 V40 Z"
        fill={colors.mark}
        stroke={colors.markDark}
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <circle cx="20" cy="16" r="2.2" fill={colors.spark} opacity="0.9" />
      <circle cx="46" cy="20" r="1.6" fill={colors.spark} opacity="0.75" />
    </svg>
  )
}

function DiamondGlyph({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={className}
      aria-hidden="true"
      style={{ display: 'block', background: 'transparent' }}
    >
      <defs>
        <linearGradient id="dia-top" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#E8FBFF" />
          <stop offset="100%" stopColor="#6AD0FF" />
        </linearGradient>
        <linearGradient id="dia-left" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#4BB8FF" />
          <stop offset="100%" stopColor="#0E6FCF" />
        </linearGradient>
        <linearGradient id="dia-right" x1="1" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7AD8FF" />
          <stop offset="100%" stopColor="#1563B8" />
        </linearGradient>
      </defs>
      {/* octahedron-style diamond, edge-to-edge, no halo */}
      <polygon points="32,4 56,28 32,60 8,28" fill="#1FA0F0" />
      <polygon points="32,4 8,28 32,28" fill="url(#dia-top)" />
      <polygon points="32,4 56,28 32,28" fill="#9FE4FF" />
      <polygon points="8,28 32,28 32,60" fill="url(#dia-left)" />
      <polygon points="56,28 32,28 32,60" fill="url(#dia-right)" />
      <polygon points="20,28 32,4 44,28" fill="#F5FDFF" opacity="0.55" />
      <circle cx="28" cy="14" r="2.4" fill="#FFFFFF" />
      <circle cx="42" cy="26" r="1.5" fill="#FFFFFF" opacity="0.85" />
    </svg>
  )
}

export default function CoinGlyph({ type, size = 28, className = '' }: Props) {
  if (type === 'diamond') {
    return <DiamondGlyph size={size} className={className} />
  }
  const key = (type in PALETTE ? type : 'gold') as keyof typeof PALETTE
  return <CoinMetal size={size} colors={PALETTE[key]} className={className} />
}
